// Hablarle a Noa en vez de escribirle. Pedido por Patricio el 26-08.
//
// 🔴 EL DICTADO LO HACE EL TELÉFONO, NO NOSOTROS. El habla se convierte a texto en
// el propio dispositivo (el reconocedor de Apple / Google) y ese texto entra por la
// MISMA puerta que si lo hubieras teclado: `noaResponde`. Cero trabajo en el
// servidor, cero audio enviado a ninguna parte, y todas las habilidades y el hilo
// de la conversación funcionan igual sin tocarlos.
//
// 🔴 Y POR ESO ESTO NO VIAJA POR AIRE. Es módulo nativo y permiso de micrófono:
// necesita binario nuevo. Va en el mismo lote que el icono, para pasar por
// TestFlight una sola vez (docs/06 §2).
//
// Se aísla en su propio componente a propósito: la pantalla del chat no tiene por
// qué saber de permisos, de eventos del reconocedor ni de idiomas.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import {
  ExpoSpeechRecognitionModule,
  useSpeechRecognitionEvent,
} from 'expo-speech-recognition';
import { C, F } from '@/constants/theme';
import OnaIcon from './OnaIcon';

/* 🔴 EL VOCABULARIO DE LA CASA (27-08). Patricio: «el microfono ahora escribe,
   no me entiende mucho pero mi voz tambien es media grave». No es su voz: es que
   «Nerja», «Palmiras», «Ogisaka», «Aucanada», «ageing» o «budget» NO ESTÁN en el
   vocabulario de ningún reconocedor. `contextualStrings` es exactamente el
   mecanismo de Apple para eso —SFSpeechRecognitionRequest.contextualStrings— y le
   dice al motor qué palabras esperar antes de oírlas.
   Los nombres de los hoteles llegan por prop desde la pantalla del chat, que ya
   los tiene cargados: así el vocabulario se actualiza solo cuando abre un hotel
   nuevo, sin tocar este fichero. */
const PALABRAS_CASA = [
  'budget', 'presupuesto', 'ageing', 'ADR', 'RevPAR', 'ocupación', 'pickup',
  'facturación', 'facturó', 'director', 'directora', 'outlet', 'outlets',
  'F&B', 'interanual', 'cobros', 'morosidad', 'Tortugona', 'Noa', 'Pulse',
  'punto de venta', 'puntos de venta', 'acumulado', 'grupo', 'zona',
];

export default function BotonVoz({ onTexto, onParcial, deshabilitado, vocabulario }: {
  /** Se llama con la frase final, cuando el usuario ha terminado de hablar. */
  onTexto: (t: string) => void;
  /** Se llama mientras habla, para que el campo enseñe lo que va diciendo. */
  onParcial?: (t: string) => void;
  deshabilitado?: boolean;
  /** Nombres propios que el reconocedor debe esperar (los hoteles). */
  vocabulario?: string[];
}) {
  const [escuchando, setEscuchando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pulso = useRef(new Animated.Value(0)).current;
  /* La última transcripción que llegó. En algunos dispositivos el evento `end`
     salta sin un `result` final, así que se guarda el mejor parcial y se usa ese:
     mejor la frase de hace 200 ms que perder lo que el usuario acaba de decir. */
  const ultima = useRef('');

  /* Late mientras escucha, con el mismo pulso de la marca. No es adorno: sin
     señal de que sigue grabando, la gente habla al vacío o corta a media frase. */
  useEffect(() => {
    if (!escuchando) { pulso.setValue(0); return; }
    const ciclo = Animated.loop(Animated.sequence([
      Animated.timing(pulso, { toValue: 1, duration: 620, easing: Easing.out(Easing.quad), useNativeDriver: true }),
      Animated.timing(pulso, { toValue: 0, duration: 620, easing: Easing.in(Easing.quad), useNativeDriver: true }),
    ]));
    ciclo.start();
    return () => ciclo.stop();
  }, [escuchando, pulso]);

  useSpeechRecognitionEvent('start', () => { setEscuchando(true); setError(null); });
  useSpeechRecognitionEvent('end', () => {
    setEscuchando(false);
    const t = ultima.current.trim();
    ultima.current = '';
    if (t) onTexto(t);
  });
  useSpeechRecognitionEvent('result', (e) => {
    const t = e.results?.[0]?.transcript ?? '';
    if (!t) return;
    ultima.current = t;
    onParcial?.(t);
  });
  useSpeechRecognitionEvent('error', (e) => {
    setEscuchando(false);
    /* 🔴 SE DICE QUÉ HA PASADO. «no-speech» es que no oyó nada y no es un fallo:
       decir «error» ahí haría que la gente dejara de usarlo. */
    const c = String(e.error ?? '');
    /* 🔴 EL CÓDIGO VA EN EL MENSAJE cuando no es uno conocido. Sin él, «no ha
       funcionado» no permite arreglar nada: el fallo del 26-08 se diagnosticó
       porque `no-speech` era la pista, y el siguiente puede ser otro. */
    setError(c === 'no-speech' ? 'No te he oído. Prueba otra vez, hablando cerca.'
      : c === 'not-allowed' || c === 'service-not-allowed'
        ? 'Sin permiso de micrófono o de dictado. Se activan en Ajustes › Ona Pulse.'
      : c === 'language-not-supported'
        ? 'Tu iPhone no tiene el español de dictado. Ajustes › General › Teclado › Dictado.'
      : `El dictado no ha funcionado (${c || 'sin código'}). Puedes escribir la pregunta.`);
  });

  const arrancar = async () => {
    setError(null);
    try {
      const permiso = await ExpoSpeechRecognitionModule.requestPermissionsAsync();
      if (!permiso.granted) {
        setError('Sin permiso de micrófono. Se activa en Ajustes.');
        return;
      }
      /* 🔴 EL RECONOCIMIENTO LOCAL, SOLO SI EL IDIOMA ESTÁ INSTALADO (26-08).
         Esto es lo que hacía que el micrófono se abriera —el punto naranja de iOS
         se encendía— y luego dijera «no te he oído»: forzaba
         `requiresOnDeviceRecognition` en iOS sin comprobar nada. Los propios docs
         del paquete lo advierten: «use getSupportedLocales() to verify if the
         locale is installed on the device prior to enabling this option». Y mi
         comentario anterior decía que si no se soportaba «cae al de red»: eso NO
         es verdad — sin el modelo de español descargado, no reconoce nada.
         Ahora se pregunta, y si el español local no está, se usa el de red, que es
         exactamente lo que hace el dictado del teclado del sistema. */
      let local = false;
      try {
        const locales = await ExpoSpeechRecognitionModule.getSupportedLocales({});
        const enDispositivo: string[] = (locales as { installedLocales?: string[] })?.installedLocales ?? [];
        local = enDispositivo.some((l) => String(l).toLowerCase().startsWith('es'));
      } catch { local = false; }

      /* Nombre completo Y nombre corto: se dice «Nerja», no «Ona Marinas de
         Nerja», y también «Cala Pi» a secas. Se le dan las dos formas porque el
         sesgo actúa sobre la frase que oye, no sobre la que está en la base. */
      const nombres = (vocabulario || []).flatMap((n) => {
        const limpio = String(n || '').trim();
        if (!limpio) return [];
        const corto = limpio.replace(/^(Ona|Hotel)\s+/i, '').trim();
        return corto && corto !== limpio ? [limpio, corto] : [limpio];
      });
      /* Apple recomienda no pasarse con la lista; 100 sobra para 49 hoteles y el
         vocabulario de la casa, y el corte evita que crezca sin darse cuenta. */
      const contexto = [...new Set([...nombres, ...PALABRAS_CASA])].slice(0, 100);

      ExpoSpeechRecognitionModule.start({
        /* En español de España, que es el idioma de toda la app. */
        lang: 'es-ES',
        /* Parciales para que el campo se vaya llenando mientras habla: sin eso,
           tres segundos de silencio en pantalla parecen que no funciona. */
        interimResults: true,
        requiresOnDeviceRecognition: local,
        continuous: false,
        contextualStrings: contexto,
        /* «search» y no «dictation»: lo que se dicta aquí son consultas cortas que
           NOMBRAN cosas («Nerja contra 2025»), no texto libre que redactar. Es el
           modo en el que Apple sesga hacia términos y nombres propios. */
        iosTaskHint: 'search',
      });
    } catch {
      setError('El dictado no está disponible en este dispositivo.');
    }
  };

  const parar = () => { try { ExpoSpeechRecognitionModule.stop(); } catch { /* ya parado */ } };

  const escala = pulso.interpolate({ inputRange: [0, 1], outputRange: [1, 1.12] });
  const halo = pulso.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0] });

  return (
    <View>
      <Pressable
        onPress={escuchando ? parar : arrancar}
        disabled={deshabilitado}
        hitSlop={6}
        accessibilityLabel={escuchando ? 'Dejar de dictar' : 'Preguntar hablando'}
        style={[s.btn, escuchando && s.btnOn, deshabilitado && { opacity: 0.4 }]}
      >
        {escuchando ? (
          <Animated.View style={[s.halo, { opacity: halo, transform: [{ scale: escala }] }]} />
        ) : null}
        {/* 🔴 Icono del juego PROPIO (docs/05), no un emoji: cada sistema dibuja
            el 🎙 a su manera y en color, y al lado de los otros veintiocho iconos
            de trazo 1.9 se veía como un parche. */}
        <OnaIcon name={escuchando ? 'parar' : 'micro'} size={19}
          color={escuchando ? '#04252A' : C.aquaSoft} accent={escuchando ? '#04252A' : C.aqua} />
      </Pressable>
      {error ? <Text style={s.err} numberOfLines={2}>{error}</Text> : null}
    </View>
  );
}

const s = StyleSheet.create({
  btn: {
    width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: C.line, backgroundColor: C.bg2, overflow: 'visible',
  },
  btnOn: { backgroundColor: C.aqua, borderColor: C.aqua },
  halo: { position: 'absolute', width: 42, height: 42, borderRadius: 21, backgroundColor: C.aqua },
  ico: { fontSize: 17, color: C.aquaSoft },
  err: { position: 'absolute', bottom: 46, right: 0, width: 190, textAlign: 'right',
         fontFamily: F.ui, fontSize: 10.5, color: C.warn, lineHeight: 14 },
});
